import React, {useEffect} from 'react';
import styles from './ClientProjectInfo.module.css';
import {projectInfoAPI, projectSampleAPI} from "../../../services/client";
import {ProjectInfoResponseData} from "../../../types/client";
import TextCard from "../../common/textCard/TextCard";
import logo from '../../../assets/covi_logo_white.png';

interface ClientProjectInfoProps {
    projectId: number;
}

//프로젝트 정보
const ClientProjectInfo: React.FC<ClientProjectInfoProps> = ({projectId}) => {
    const [data, setData] = React.useState<ProjectInfoResponseData | null>(null);
    const [sample, setSample] = React.useState<string>();
    const [loading, setLoading] = React.useState(true);

    const fetchData = async () => {
        try {
            setLoading(true);
            //프로젝트 정보 조회 API
            const response = await projectInfoAPI(projectId);
            setData(response);
            //프로젝트 샘플 데이터 조회 API
            const file = await projectSampleAPI(projectId);
            //파일 내부 URL 생성
            const url = URL.createObjectURL(file);
            setSample(url);
        } catch (error) {
            console.error('Error fetching project:', error);
        } finally {
            setLoading(false);
        }
    };

    //요일
    const formatDate = (date: Date | undefined, day: number | undefined) => {
        if (!date || day === undefined) return '-';
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    useEffect(() => {
        fetchData();
    }, [projectId]);

    //날짜 관련 정보
    const dateInfo = [
        {
            label: "최초 생성일",
            value: formatDate(data?.createdAt, data?.createDay)
        },
        {
            label: "작업 시작일",
            value: formatDate(data?.startedAt, data?.startDay)
        },
        {
            label: "작업 종료일",
            value: formatDate(data?.deadline, data?.deadlineDay)
        }
    ];

    //프로젝트 정보
    const projectInfo = [
        {
            label: "진행 상태",
            value: data?.projectStatus?.toString() || '-'
        },
        {
            label: "데이터 수량",
            value: data?.dataAmount ? `${data.dataAmount.toLocaleString()}장` : '-'
        },
        {
            label: "가공 방식",
            value: data?.projectType?.toString() || '-'
        },
        {
            label: "작업자 수",
            value: data?.workerAmount?.toLocaleString() + '명' || '-'
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h2 className={styles.title}>Project Information</h2>
                <p className={styles.subtitle}>프로젝트 진행 정보</p>

                <div className={styles.infoContainer}>
                    <div className={styles.leftSection}>
                        <div className={styles.progressCircle}>
                            {/*프로젝트 마감일 기준 작업 달성률 원형 그래프*/}
                            <svg viewBox="0 0 36 36" className={styles.circularChart}>
                                <path
                                    d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#eee"
                                    strokeWidth="3"
                                />
                                <path
                                    d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#002fa7"
                                    strokeWidth="3"
                                    strokeDasharray={`${data?.progress || 0}, 100`}
                                    transform="rotate(90 18 18)"
                                />
                            </svg>
                            <div className={styles.progressText}>
                                {/*작업 달성률 %*/}
                                <span className={styles.percentage}>{data?.progress || 0}%</span>
                                <span className={styles.label}>작업 달성률</span>
                            </div>
                        </div>
                        {/*프로젝트명*/}
                        <h3 className={styles.projectName}>{data?.projectName || '-'}</h3>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.imageContainer}>
                            {/*샘플 데이터*/}
                            {sample ? (
                                <img
                                    src={sample}
                                    alt="Project Preview"
                                    className={styles.projectImage}
                                    onError={(e) => {
                                        e.currentTarget.src = logo;
                                    }}
                                />
                            ) : (
                                <div className={styles.noImage}>No image available</div>
                            )}
                        </div>
                        {/*프로젝트 정보*/}
                        <div className={styles.infoGrid}>
                            <TextCard items={dateInfo} className={styles.fullWidth}/>
                            <TextCard items={projectInfo} className={styles.fullWidth}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ClientProjectInfo;